import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import Calendar from "./Calendar";
import OrderCard from "./OrderCard";
import ConfirmModal from "./ConfirmModal";
import Notification from "../utility/Notification";
import {
  getClients,
  checkOrderByDayMonthYear,
  deleteOrders,
  getAllClientDebts,
  setClientDebt,
  getOrdersForMonth,
} from "../utility/EndPointGoogleFirebase";
import { handleSaveOrders } from "./HandlerOrders";

const Orders = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState({});
  const [editOrders, setEditOrders] = useState({});
  const [notification, setNotification] = useState(null);
  const [readOnlyClients, setReadOnlyClients] = useState({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientDebts, setClientDebts] = useState({});

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
        const clientsData = await getClients();
        setClients(clientsData);
        const debts = await getAllClientDebts();
        setClientDebts(debts);
      } catch (error) {
        setNotification({ type: "error", message: "Errore durante il recupero dei clienti." });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();

    window.addEventListener("clientsUpdated", fetchClients);
    return () => window.removeEventListener("clientsUpdated", fetchClients);
  }, []);

  const fetchOrdersForClients = async () => {
    setIsLoading(true);
    setEditOrders({});
    setReadOnlyClients({});

    const date = dayjs(selectedDate);
    const [day, month, year] = date.format("D-M-YYYY").split("-");

    try {
      // ✅ Carica tutti gli ordini del mese per colorare il calendario
      const allOrders = await getOrdersForMonth(parseInt(month), parseInt(year));
      const ordersMap = allOrders.reduce((acc, o) => {
        const key = `${o.anno}-${String(o.mese).padStart(2, "0")}-${String(o.giorno).padStart(2, "0")}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(o);
        return acc;
      }, {});
      setOrders(ordersMap);

      // Carica gli ordini del giorno selezionato per le card clienti
      for (const client of clients) {
        const result = await checkOrderByDayMonthYear(client, day, month, year);
        if (result && !result.empty) {
          result.forEach((doc) => {
            const orderData = { ...doc.data(), id: doc.id };
            setEditOrders((prev) => ({ ...prev, [client]: orderData }));
            setReadOnlyClients((prev) => ({ ...prev, [client]: true }));
          });
        }
      }
    } catch (error) {
      setNotification({ type: "error", message: "Errore durante il caricamento degli ordini." });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdersForClients();
  }, [selectedDate, clients]);

  const handleDelete = async (clientName) => {
    try {
      setIsLoading(true);
      const order = editOrders[clientName];
      if (order && order.id) {
        await deleteOrders(order.id);
        setEditOrders((prev) => { const u = { ...prev }; delete u[clientName]; return u; });
        setOrders((prev) => {
          const u = { ...prev };
          if (u[selectedDate]) {
            delete u[selectedDate][clientName];
            if (Object.keys(u[selectedDate]).length === 0) delete u[selectedDate];
          }
          return u;
        });
        setReadOnlyClients((prev) => ({ ...prev, [clientName]: false }));
        setNotification({ type: "success", message: `Ordine per ${clientName} eliminato.` });
      } else {
        throw new Error(`Ordine non trovato per ${clientName}`);
      }
    } catch (error) {
      setNotification({ type: "error", message: `Errore eliminazione ordine per ${clientName}.` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // ✅ Salva sempre i debiti, indipendentemente dalla presenza di ordini
      const debtPromises = Object.entries(clientDebts).map(([clientName, debtValue]) =>
        setClientDebt(clientName, debtValue)
      );
      await Promise.all(debtPromises);

      // Se non ci sono ordini, notifica solo il salvataggio del saldo e termina
      if (Object.keys(editOrders).length === 0) {
        setNotification({ type: "success", message: "Saldo precedente salvato." });
        setIsProcessing(false);
        return;
      }

      // Salva gli ordini
      const promises = Object.entries(editOrders).map(async ([clientName, inputValues]) => {
        if (!readOnlyClients[clientName]) {
          await handleSaveOrders(
            { ...inputValues, default: inputValues.default || 3 },
            clientName, selectedDate,
            fetchOrdersForClients,
            () => setEditOrders({}),
            setNotification
          );
          setOrders((prev) => ({
            ...prev,
            [selectedDate]: [...(prev[selectedDate] || []), { cliente: clientName }],
          }));
        }
      });
      await Promise.all(promises);
      setNotification({
        type: "success",
        message: `Ordini salvati per il ${dayjs(selectedDate).format("DD/MM/YYYY")}.`,
      });
    } catch (error) {
      setNotification({ type: "error", message: "Errore durante il salvataggio." });
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleEditOrder = (clientName) => {
    setReadOnlyClients((prev) => ({ ...prev, [clientName]: !prev[clientName] }));
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');

        * { box-sizing: border-box; }

        .orders-loader-ring {
          width: 36px; height: 36px;
          border: 2px solid #e5e7eb; border-top-color: #4f46e5;
          border-radius: 50%;
          animation: spinRing 0.8s linear infinite;
        }
        @keyframes spinRing { to { transform: rotate(360deg); } }

        .save-btn {
          position: relative; overflow: hidden;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .save-btn:not(:disabled):active { transform: scale(0.98); }
        @media (hover: hover) {
          .save-btn:not(:disabled):hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 24px rgba(79,70,229,0.25);
          }
        }

        .orders-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }
        @media (min-width: 600px) {
          .orders-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
        }
        @media (min-width: 1024px) {
          .orders-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; }
        }

        .orders-page-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px 12px;
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-bottom: max(32px, env(safe-area-inset-bottom));
        }
        @media (min-width: 480px) { .orders-page-wrapper { padding: 20px 16px; } }
        @media (min-width: 768px) { .orders-page-wrapper { padding: 28px 24px; } }
        @media (min-width: 1024px) { .orders-page-wrapper { padding: 36px 32px; } }

        .section-card {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        .section-card-calendar {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          overflow: hidden;
        }

        .section-header {
          padding: 14px 16px;
          border-bottom: 1px solid #f3f4f6;
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
          border-radius: 14px 14px 0 0;
        }
        @media (min-width: 480px) { .section-header { padding: 16px 20px; } }

        .section-body {
          padding: 12px;
        }
        @media (min-width: 480px) { .section-body { padding: 16px; } }
        @media (min-width: 768px) { .section-body { padding: 20px; } }

        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 5vw, 36px);
          font-weight: 400; color: #111827; line-height: 1.2;
        }

        .save-btn-wrapper {
          margin-top: 20px;
          display: flex; justify-content: center;
        }
        @media (min-width: 768px) { .save-btn-wrapper { margin-top: 28px; } }
      `}</style>

      {isLoading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
          background: "rgba(249,250,251,0.88)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}>
          <div className="orders-loader-ring" />
          <p style={{ fontSize: 12, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Attendere
          </p>
        </div>
      )}

      <Notification notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onConfirm={() => { setIsConfirmModalOpen(false); handleDelete(clientToDelete); }}
        onCancel={() => setIsConfirmModalOpen(false)}
        clientName={clientToDelete}
      />

      <div className="orders-page-wrapper">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Header */}
          <div style={{ textAlign: "center", paddingBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>
              Gestione
            </p>
            <h1 className="page-title">Ordini Clienti</h1>
            <div style={{ width: 28, height: 2, background: "#4f46e5", borderRadius: 2, margin: "10px auto 0" }} />
          </div>

          {/* Calendar */}
          <div className="section-card-calendar">
            <Calendar
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              orders={orders}
            />
          </div>

          {/* Orders section */}
          <div className="section-card">
            <div className="section-header">
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 500, color: "#111827", margin: 0 }}>
                  Ordini del giorno
                </h2>
                <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, margin: 0 }}>
                  {dayjs(selectedDate).format("dddd D MMMM YYYY")}
                </p>
              </div>
              <div style={{
                fontSize: 12, fontWeight: 500, flexShrink: 0,
                background: "#eef2ff", color: "#4f46e5",
                padding: "4px 12px", borderRadius: 100,
                border: "1px solid #c7d2fe",
              }}>
                {clients.length} clienti
              </div>
            </div>

            <div className="section-body">
              <div className="orders-grid">
                {clients.map((client) => (
                  <OrderCard
                    key={client}
                    clientName={client}
                    orderData={editOrders[client] || {}}
                    onChange={(clientName, field, value) => {
                      setEditOrders((prev) => ({
                        ...prev,
                        [clientName]: { ...prev[clientName], [field]: value },
                      }));
                    }}
                    onDelete={(clientName) => {
                      setClientToDelete(clientName);
                      setIsConfirmModalOpen(true);
                    }}
                    onEdit={toggleEditOrder}
                    readOnly={readOnlyClients[client] || false}
                    debt={clientDebts[client] ?? 0}
                    onDebtChange={(clientName, newDebt) =>
                      setClientDebts((prev) => ({ ...prev, [clientName]: newDebt }))
                    }
                  />
                ))}
              </div>

              <div className="save-btn-wrapper">
                <button
                  onClick={handleSave}
                  disabled={isProcessing}
                  className="save-btn"
                  style={{
                    width: "100%", maxWidth: 400,
                    padding: "15px 32px", borderRadius: 10, border: "none",
                    fontSize: 15, fontWeight: 500, letterSpacing: "0.03em",
                    cursor: isProcessing ? "not-allowed" : "pointer",
                    background: isProcessing ? "#e5e7eb" : "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
                    color: isProcessing ? "#9ca3af" : "#fff",
                    transition: "all 0.2s ease",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {isProcessing ? (
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                      <span style={{
                        width: 14, height: 14,
                        border: "2px solid #d1d5db", borderTopColor: "#9ca3af",
                        borderRadius: "50%", display: "inline-block",
                        animation: "spinRing 0.8s linear infinite",
                      }} />
                      Salvataggio...
                    </span>
                  ) : "Salva Ordini"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Orders;